import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { SelectionSet, SelectionSetInput, SelectionSetLabel, tokensRaw } from '@vp/swan';
import styled from 'styled-components';

export type OptionType = {
    id: string;
    label: string;
};

interface ScrollableSelectionSetProps {
    options: OptionType[];
    setSelectedValue: (value: string | null) => void;
    selectedValue: string;
}

const Container = styled.div`
    position: relative;
`;

const GradientOverlay = styled.div<{ left?: boolean }>`
    position: absolute;
    top: 0;
    bottom: 0;
    width: ${tokensRaw.SwanSemSpace9};
    pointer-events: none;
    z-index: 10;
    background-image: ${({ left }) =>
            left
                ? `linear-gradient(270deg, transparent, ${tokensRaw.SwanSemColorBgContainer});`
                : `linear-gradient(90deg, transparent, ${tokensRaw.SwanSemColorBgContainer});`}
        ${({ left }) => (left ? 'left: 0;' : 'right: 0;')};
`;

const StyledSelectionSet = styled(SelectionSet)`
    flex-flow: row;
    flex-wrap: nowrap !important;
    overflow-x: auto;
    padding: ${tokensRaw.SwanSemSpace1};
    scrollbar-width: thin;

    > label {
        word-break: unset;
        white-space: nowrap;
    }
`;

export function ScrollableSelectionSet({
    options,
    selectedValue,
    setSelectedValue,
}: ScrollableSelectionSetProps) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const firstOptionRef = useRef<HTMLDivElement>(null);
    const lastOptionRef = useRef<HTMLDivElement>(null);

    const { ref: firstOptionRefFn, inView: firstOptionInView } = useInView({
        root: scrollContainerRef.current,
        threshold: 1,
    });

    const { ref: lastOptionRefFn, inView: lastOptionInView } = useInView({
        root: scrollContainerRef.current,
        threshold: 1,
    });

    useEffect(() => {
        if (scrollContainerRef.current) {
            firstOptionRefFn(firstOptionRef.current);
            lastOptionRefFn(lastOptionRef.current);
        }
    }, [firstOptionRefFn, lastOptionRefFn]);

    return (
        <Container>
            {!firstOptionInView && <GradientOverlay left data-testid="gradient-left" />}
            {!lastOptionInView && <GradientOverlay data-testid="gradient-right" />}
            <StyledSelectionSet
                variant="single-select"
                skin="buttons"
                selectedValue={selectedValue}
                mb={6}
                ref={scrollContainerRef}
                onSelectedValueChange={(selectedValue: string | null) =>
                    setSelectedValue(selectedValue)
                }
            >
                {options.map(({ id, label }, index) => (
                    <SelectionSetInput data-testid={id} value={id} key={id}>
                        <SelectionSetLabel
                            ref={
                                index === 0
                                    ? firstOptionRef
                                    : index === options.length - 1
                                    ? lastOptionRef
                                    : null
                            }
                            mb={0}
                        >
                            {label}
                        </SelectionSetLabel>
                    </SelectionSetInput>
                ))}
            </StyledSelectionSet>
        </Container>
    );
}
